import { AnimatePresence, motion } from 'framer-motion'
import { CheckCheck, PackagePlus, Search, Tag, Trash2, X } from 'lucide-react'
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { MdCheckBox, MdCheckBoxOutlineBlank, MdIndeterminateCheckBox } from 'react-icons/md'
import Select from 'react-select'
import { Link } from 'react-router'
import { toast } from 'react-toastify'
import { BASE_URL_API } from '../../References/URLs'
import ActiveStoreContext from '../../store/ActiveStoreContext'
import AuthContext from '../../store/auth-context'
import styles from './ImHome.module.css'

/* ─────────────────── helpers ─────────────────── */
const fmt = (n) => {
    const num = parseFloat(n)
    return isNaN(num) ? '—' : num.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

const ImHome = () => {
    const authCtx = useContext(AuthContext)
    const asCtx = useContext(ActiveStoreContext)
    const activeStore = (() => {
        try { return (asCtx?.activeStore && asCtx.activeStore.trim() !== "") ? JSON.parse(asCtx.activeStore) : null }
        catch { return null }
    })()
    const vendorIdentifier = JSON.parse(authCtx?.loginData || '{}')?.partnersData?.vendorIdentifier

    /* ── data ── */
    const [productsData, setProductsData] = useState([])
    const [filteredData, setFilteredData] = useState([])
    const [isLoading, setIsLoading] = useState(true)
    const [searchKey, setSearchKey] = useState('')

    /* ── selection ── */
    const [selectedItems, setSelectedItems] = useState([])

    /* ── bulk actions UI ── */
    const [bulkPanel, setBulkPanel] = useState(null) // null | "category" | "hsn"

    /* ── per-row inline HSN edit ── */
    const [editingHsnId, setEditingHsnId] = useState(null)
    const [hsnDraft, setHsnDraft] = useState('')

    /* ── inline HSN saving state ── */
    const [savingHsnId, setSavingHsnId] = useState(null)

    /* ── bulk apply states ── */
    const [bulkHsnValue, setBulkHsnValue] = useState('')
    const [bulkCategoryValue, setBulkCategoryValue] = useState('')
    const [isApplyingBulk, setIsApplyingBulk] = useState(false)

    /* ── category list ── */
    const [categories, setCategories] = useState([])
    const [isCategoriesLoading, setIsCategoriesLoading] = useState(true)
    const [hsnCodes, setHsnCodes] = useState([])
    const [isHsnCodesLoading, setIsHsnCodesLoading] = useState(true)

    /* ─────────── API ─────────── */
    const getInventoryCategories = async () => {
        setIsCategoriesLoading(true)
        try {
            const response = await fetch(BASE_URL_API + "getInventoryCategories", {
                method: "POST",
                body: JSON.stringify({
                    token: authCtx?.token,
                    vendorIdentifier,
                    storeIdentifier: activeStore?.storeIdentifier
                })
            })
            if (!response.ok) throw new Error("Server Error")
            const respo = await response.json()
            if (respo?.status === "success") {
                setCategories(respo?.response)
            } else {
                if (respo?.message === "tokenExpired") authCtx.logout()
                setCategories([])
            }
        } catch (e) {
            console.error(e)
            setCategories([])
        } finally {
            setIsCategoriesLoading(false)
        }
    }

    const getHsnSacCodes = async () => {
        setIsHsnCodesLoading(true)
        try {
            const response = await fetch(BASE_URL_API + "getHsnSacCodes", {
                method: "POST",
                body: JSON.stringify({
                    token: authCtx?.token,
                })
            })
            if (!response.ok) throw new Error("Server Error")
            const respo = await response.json()

            if (respo?.status === "success") {
                const candidates = [
                    respo?.response,
                    respo?.response?.hsnSacCodes,
                    respo?.response?.hsnCodes,
                    respo?.response?.data,
                ]
                const rawList = candidates.find(Array.isArray) || []

                const normalized = rawList
                    .map((item) => {
                        if (!item) return null
                        if (typeof item === 'string' || typeof item === 'number') {
                            return {
                                code: String(item).trim(),
                                code_name: '',
                                description: '',
                                gst_rate: '',
                            }
                        }

                        const code = String(item?.code || item?.hsnCode || item?.hsn || '').trim()
                        if (!code) return null

                        return {
                            code,
                            code_name: String(item?.code_name || item?.codeName || '').trim(),
                            description: String(item?.description || '').trim(),
                            gst_rate: String(item?.gst_rate || item?.gstRate || '').trim(),
                            service_category: item?.service_category || null,
                            popularity_score: item?.popularity_score || '',
                        }
                    })
                    .filter(Boolean)

                const uniqueMap = new Map()
                normalized.forEach((entry) => {
                    if (!uniqueMap.has(entry.code)) {
                        uniqueMap.set(entry.code, entry)
                    }
                })

                setHsnCodes(Array.from(uniqueMap.values()))
            } else {
                if (respo?.message === "tokenExpired") authCtx.logout()
                setHsnCodes([])
            }
        } catch (e) {
            console.error(e)
            setHsnCodes([])
        } finally {
            setIsHsnCodesLoading(false)
        }
    }

    const getInventoryProducts = async () => {
        setIsLoading(true)
        try {
            const response = await fetch(BASE_URL_API + "getInventoryProducts", {
                method: "POST",
                body: JSON.stringify({
                    token: authCtx?.token,
                    vendorIdentifier,
                    storeIdentifier: activeStore?.storeIdentifier
                })
            })
            if (!response.ok) throw new Error("Server Error")
            const respo = await response.json()
            if (respo?.status === "success") {
                setProductsData(respo?.response)
                setFilteredData(respo?.response)
            } else {
                if (respo?.message === "tokenExpired") authCtx.logout()
                setProductsData([])
                setFilteredData([])
            }
        } catch (e) {
            console.error(e)
            setProductsData([])
            setFilteredData([])
        } finally {
            setIsLoading(false)
        }
    }

    useEffect(() => {
        if (activeStore?.storeIdentifier) {
            getInventoryProducts()
            getInventoryCategories()
            getHsnSacCodes()
        }
    }, [activeStore?.storeIdentifier])

    /* ─────────── Search filter ─────────── */
    const filterData = useCallback((data, key) => {
        if (!key.trim()) return setFilteredData(data)
        setFilteredData(data.filter(item =>
            Object.values(item).some(v =>
                v?.toString().toLowerCase().includes(key.toLowerCase())
            )
        ))
    }, [])

    useEffect(() => {
        filterData(productsData, searchKey)
    }, [searchKey, productsData, filterData])

    /* ─────────── Selection helpers ─────────── */
    const isSelected = (id) => selectedItems.some(i => i.productIdentifier === id)
    const allSelected = filteredData.length > 0 && selectedItems.length === filteredData.length
    const someSelected = selectedItems.length > 0 && !allSelected

    const toggleSelect = (item) => {
        const exists = selectedItems.find(p => p.productIdentifier === item.productIdentifier)
        setSelectedItems(exists
            ? selectedItems.filter(p => p.productIdentifier !== item.productIdentifier)
            : [...selectedItems, item]
        )
    }

    const toggleSelectAll = () => {
        if (allSelected) setSelectedItems([])
        else setSelectedItems([...filteredData])
    }

    const clearSelection = () => {
        setSelectedItems([])
        setBulkPanel(null)
    }

    /* ─────────── Inline HSN save ─────────── */
    const saveHsn = async (item) => {
        if (!hsnDraft) {
            toast.warning("Please select an HSN code.")
            return
        }
        setSavingHsnId(item.productIdentifier)
        try {
            const response = await fetch(BASE_URL_API + "updateInventoryProductHsn", {
                method: "POST",
                body: JSON.stringify({
                    token: authCtx?.token,
                    vendorIdentifier,
                    storeIdentifier: activeStore?.storeIdentifier,
                    productIdentifier: item.productIdentifier,
                    hsnCode: hsnDraft
                })
            })
            const respo = await response.json()
            if (respo?.status === "success") {
                setProductsData(prev => prev.map(p =>
                    p.productIdentifier === item.productIdentifier ? { ...p, hsnCode: hsnDraft } : p
                ))
                toast.success("HSN code updated!")
            } else {
                if (respo?.message === "tokenExpired") {
                    authCtx.logout()
                    return
                }
                toast.warning(respo?.message || "Failed to update HSN code.")
            }
        } catch {
            toast.warning("Could not save HSN code.")
        } finally {
            setSavingHsnId(null)
            setEditingHsnId(null)
            setHsnDraft('')
        }
    }

    const startHsnEdit = (item) => {
        setEditingHsnId(item.productIdentifier)
        setHsnDraft(item.hsnCode || '')
    }

    const cancelHsnEdit = () => {
        setEditingHsnId(null)
        setHsnDraft('')
    }

    /* ─────────── Bulk HSN apply ─────────── */
    const applyBulkHsn = async () => {
        if (!bulkHsnValue) {
            toast.warning("Please enter an HSN code.")
            return
        }
        setIsApplyingBulk(true)
        try {
            const response = await fetch(BASE_URL_API + "bulkUpdateInventoryProductHsn", {
                method: "POST",
                body: JSON.stringify({
                    token: authCtx?.token,
                    vendorIdentifier,
                    storeIdentifier: activeStore?.storeIdentifier,
                    products: selectedItems.map(i => i.productIdentifier),
                    hsnCode: bulkHsnValue
                })
            })
            const respo = await response.json()
            if (respo?.status === "success") {
                const ids = new Set(selectedItems.map(i => i.productIdentifier))
                setProductsData(prev => prev.map(p =>
                    ids.has(p.productIdentifier) ? { ...p, hsnCode: bulkHsnValue } : p
                ))
                toast.success(`HSN updated for ${selectedItems.length} product(s).`)
                clearSelection()
                setBulkHsnValue('')
            } else {
                if (respo?.message === "tokenExpired") {
                    authCtx.logout()
                    return
                }
                toast.warning(respo?.message || "Bulk HSN update failed.")
            }
        } catch {
            toast.warning("Could not apply bulk HSN update.")
        } finally {
            setIsApplyingBulk(false)
        }
    }

    /* ─────────── Bulk Category apply ─────────── */
    const applyBulkCategory = async () => {
        if (!bulkCategoryValue) {
            toast.warning("Please select a category.")
            return
        }
        const cat = categories.find(c => c.categoryIdentifier === bulkCategoryValue)
        setIsApplyingBulk(true)
        try {
            const response = await fetch(BASE_URL_API + "bulkUpdateInventoryProductCategory", {
                method: "POST",
                body: JSON.stringify({
                    token: authCtx?.token,
                    vendorIdentifier,
                    storeIdentifier: activeStore?.storeIdentifier,
                    products: selectedItems.map(i => i.productIdentifier),
                    categoryIdentifier: bulkCategoryValue,
                    categoryName: cat?.categoryName || ""
                })
            })
            const respo = await response.json()
            if (respo?.status === "success") {
                const ids = new Set(selectedItems.map(i => i.productIdentifier))
                setProductsData(prev => prev.map(p =>
                    ids.has(p.productIdentifier) ? { ...p, categoryIdentifier: bulkCategoryValue, categoryName: cat?.categoryName || "" } : p
                ))
                toast.success(`Category updated for ${selectedItems.length} product(s).`)
                clearSelection()
                setBulkCategoryValue('')
            } else {
                if (respo?.message === "tokenExpired") {
                    authCtx.logout()
                    return
                }
                toast.warning(respo?.message || "Bulk category update failed.")
            }
        } catch {
            toast.warning("Could not apply bulk category update.")
        } finally {
            setIsApplyingBulk(false)
        }
    }

    /* ─────────── Category options from API ─────────── */
    const categoryOptions = useMemo(() => categories, [categories])
    const categorySelectOptions = useMemo(() => {
        return categoryOptions.map((entry) => ({
            value: entry.categoryIdentifier,
            label: entry.categoryName,
            description: entry.description,
            iconTag: entry.iconTag,
        }))
    }, [categoryOptions])

    const selectedCategoryOption = useMemo(() => {
        return categorySelectOptions.find((option) => option.value === bulkCategoryValue) || null
    }, [categorySelectOptions, bulkCategoryValue])

    const hsnOptions = useMemo(() => {
        return hsnCodes.map((entry) => {
            const labelParts = [entry.code]
            if (entry.code_name) labelParts.push(entry.code_name)
            return {
                value: entry.code,
                label: labelParts.join(' · '),
                description: entry.description,
                gstRate: entry.gst_rate,
            }
        })
    }, [hsnCodes])

    const selectStyles = useMemo(() => ({
        container: (base) => ({
            ...base,
            width: '100%',
            minWidth: 0,
        }),
        control: (base) => ({
            ...base,
            minHeight: 34,
            height: 34,
            borderColor: '#c7d2fe',
            backgroundColor: '#ffffff',
            borderRadius: 8,
            boxShadow: 'none',
            '&:hover': { borderColor: '#a5b4fc' }
        }),
        singleValue: (base) => ({
            ...base,
            color: '#1e293b',
            fontSize: '0.82rem',
            fontWeight: 600,
        }),
        placeholder: (base) => ({
            ...base,
            color: '#94a3b8',
            fontSize: '0.8rem',
        }),
        valueContainer: (base) => ({ ...base, padding: '0 8px', height: 34 }),
        input: (base) => ({ ...base, margin: 0, padding: 0 }),
        indicatorsContainer: (base) => ({ ...base, height: 34 }),
        indicatorSeparator: () => ({ display: 'none' }),
        dropdownIndicator: (base, state) => ({
            ...base,
            color: state.isFocused ? '#6366f1' : '#94a3b8',
            padding: 6,
            '&:hover': { color: '#4f46e5' }
        }),
        clearIndicator: (base) => ({ ...base, padding: 6 }),
        menu: (base) => ({
            ...base,
            border: '1px solid #e2e8f0',
            borderRadius: 10,
            overflow: 'hidden',
            boxShadow: '0 10px 24px rgba(37, 46, 86, 0.12)',
        }),
        menuList: (base) => ({ ...base, maxHeight: 240 }),
        menuPortal: (base) => ({ ...base, zIndex: 20000 }),
        option: (base, state) => ({
            ...base,
            fontSize: '0.82rem',
            backgroundColor: state.isSelected ? '#e0e7ff' : state.isFocused ? '#eef2ff' : '#fff',
            color: '#1e293b',
            cursor: 'pointer'
        })
    }), [])

    /* ─────────── No store guard ─────────── */
    if (activeStore === null) {
        return (
            <div className={styles.noStoreWrapper}>
                <div className={styles.noStoreCard}>
                    <PackagePlus size={36} className={styles.noStoreIcon} />
                    <h3>No Store Selected</h3>
                    <p>Please select a store first to view your product catalogue.</p>
                </div>
            </div>
        )
    }

    return (
        <div className={styles.mainWrapper}>

            {/* ── Top Header ── */}
            <div className={styles.pageHeader}>
                <div className={styles.pageHeaderLeft}>
                    <div className={styles.statusPill}>Inventory</div>
                    <h1 className={styles.pageTitle}>Product Catalogue</h1>
                    <p className={styles.pageSubtitle}>Manage, search, and update your entire product inventory. Only the products with category <b>Retail</b> will appear while creating invoice for selling purpose.</p>
                </div>
                <div className={styles.pageHeaderRight}>
                    <Link className={styles.primaryBtn} to={`/manage/inventory/add-products`}>
                        <PackagePlus size={16} /> Add Product
                    </Link>
                    <Link className={styles.secondaryBtn} to={`/manage/inventory/add-stock`}>
                        <CheckCheck size={16} /> Update Stock
                    </Link>
                </div>
            </div>

            {/* ── Search + meta bar ── */}
            <div className={styles.controlBar}>
                <div className={styles.searchBox}>
                    <Search size={15} className={styles.searchIcon} />
                    <input
                        className={styles.searchInput}
                        placeholder="Search by name, SKU, category, HSN…"
                        value={searchKey}
                        onChange={e => setSearchKey(e.target.value)}
                    />
                    {searchKey && (
                        <button className={styles.clearSearch} onClick={() => setSearchKey('')}>
                            <X size={14} />
                        </button>
                    )}
                </div>
                <div className={styles.metaBadge}>
                    {isLoading ? "Loading…" : `${filteredData.length} product${filteredData.length !== 1 ? 's' : ''}`}
                </div>
            </div>

            {/* ── Table ── */}
            <div className={styles.tableCard}>
                {/* Table Header */}
                <div className={styles.tableHeader}>
                    <div className={styles.colCheck} onClick={toggleSelectAll}>
                        {allSelected
                            ? <MdCheckBox size={18} color="#6d28d9" />
                            : someSelected
                                ? <MdIndeterminateCheckBox size={18} color="#6d28d9" />
                                : <MdCheckBoxOutlineBlank size={18} color="#94a3b8" />
                        }
                    </div>
                    <div className={styles.colSn}>#</div>
                    <div className={styles.colProduct}>Product</div>
                    <div className={styles.colCategory}>Category</div>
                    <div className={styles.colBarcode}>SKU / Barcode</div>
                    <div className={styles.colMrp}>MRP</div>
                    <div className={styles.colPrice}>Selling Price</div>
                    <div className={styles.colDiscount}>Disc %</div>
                    <div className={styles.colHsn}>HSN</div>
                    <div className={styles.colAction}>Action</div>
                </div>

                {/* Table Body */}
                <div className={styles.tableBody}>
                    {isLoading ? (
                        <div className={styles.stateWrapper}>
                            <div className={styles.spinnerRing} />
                            <p>Loading products…</p>
                        </div>
                    ) : filteredData.length === 0 ? (
                        <motion.div
                            className={styles.emptyState}
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                        >
                            <PackagePlus size={40} className={styles.emptyIcon} />
                            <h3>No products found</h3>
                            <p>{searchKey ? "Try a different search term." : "Start by adding products to your inventory."}</p>
                            {!searchKey && (
                                <Link className={styles.primaryBtn} to="/manage/inventory/add-products">
                                    <PackagePlus size={15} /> Add Your First Product
                                </Link>
                            )}
                        </motion.div>
                    ) : (
                        filteredData.map((item, index) => {
                            const selected = isSelected(item.productIdentifier)
                            const isEditingHsn = editingHsnId === item.productIdentifier
                            const isSavingHsn = savingHsnId === item.productIdentifier

                            return (
                                <motion.div
                                    key={item.productIdentifier || index}
                                    className={`${styles.tableRow} ${selected ? styles.rowSelected : ''}`}
                                    initial={{ opacity: 0, y: 6 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: Math.min(index * 0.025, 0.4) }}
                                >
                                    {/* Checkbox */}
                                    <div className={styles.colCheck} onClick={() => toggleSelect(item)}>
                                        {selected
                                            ? <MdCheckBox size={18} color="#6d28d9" />
                                            : <MdCheckBoxOutlineBlank size={18} color="#94a3b8" />
                                        }
                                    </div>

                                    {/* # */}
                                    <div className={styles.colSn}>{index + 1}</div>

                                    {/* Product name */}
                                    <div className={styles.colProduct}>
                                        <span className={styles.productName}>{item?.productName}</span>
                                    </div>

                                    {/* Category */}
                                    <div className={styles.colCategory}>
                                        {item?.categoryName
                                            ? <span className={styles.categoryPill}>{item.categoryName}</span>
                                            : <span className={styles.emptyCell}>—</span>
                                        }
                                    </div>

                                    {/* SKU / Barcode */}
                                    <div className={styles.colBarcode}>
                                        <span className={styles.skuText}>{item?.skuCode || item?.barCode || '—'}</span>
                                    </div>

                                    {/* MRP */}
                                    <div className={styles.colMrp}>
                                        <span className={styles.priceText}>₹{fmt(item?.printedMrp)}</span>
                                    </div>

                                    {/* Selling Price */}
                                    <div className={styles.colPrice}>
                                        <span className={styles.priceText}>₹{fmt(item?.retailPrice)}</span>
                                    </div>

                                    {/* Discount */}
                                    <div className={styles.colDiscount}>
                                        {item?.discountPercentage
                                            ? <span className={styles.discBadge}>{item.discountPercentage}%</span>
                                            : <span className={styles.emptyCell}>—</span>
                                        }
                                    </div>

                                    {/* HSN — inline edit */}
                                    <div className={styles.colHsn}>
                                        {isEditingHsn ? (
                                            <div className={styles.hsnEditRow}>
                                                <div className={styles.hsnSelectInline}>
                                                    <Select
                                                        classNamePrefix="hsn-select"
                                                        styles={selectStyles}
                                                        options={hsnOptions}
                                                        isLoading={isHsnCodesLoading}
                                                        isClearable={false}
                                                        menuPlacement="top"
                                                        menuPosition="fixed"
                                                        menuShouldScrollIntoView={false}
                                                        menuPortalTarget={document.body}
                                                        placeholder="Select HSN"
                                                        value={hsnOptions.find(option => option.value === hsnDraft) || null}
                                                        onChange={(option) => setHsnDraft(option?.value || '')}
                                                        noOptionsMessage={() => isHsnCodesLoading ? 'Loading HSN codes…' : 'No HSN codes found'}
                                                    />
                                                </div>
                                                <button
                                                    className={styles.hsnSaveBtn}
                                                    onClick={() => saveHsn(item)}
                                                    disabled={isSavingHsn}
                                                >
                                                    {isSavingHsn ? '…' : '✓'}
                                                </button>
                                                <button className={styles.hsnCancelBtn} onClick={cancelHsnEdit}>
                                                    <X size={12} />
                                                </button>
                                            </div>
                                        ) : (
                                            <span
                                                className={item?.hsnCode ? styles.hsnPill : styles.hsnEmpty}
                                                onClick={() => startHsnEdit(item)}
                                                title="Click to edit HSN"
                                            >
                                                {item?.hsnCode || '+ HSN'}
                                            </span>
                                        )}
                                    </div>

                                    {/* Action */}
                                    <div className={styles.colAction}>
                                        <button
                                            className={styles.actionBtn}
                                            title="Edit HSN Code"
                                            onClick={() => startHsnEdit(item)}
                                        >
                                            <Tag size={14} />
                                        </button>
                                    </div>
                                </motion.div>
                            )
                        })
                    )}
                </div>
            </div>

            {/* ── Floating Bulk Toolbar ── */}
            <AnimatePresence>
                {selectedItems.length > 0 && (
                    <motion.div
                        className={styles.bulkToolbar}
                        initial={{ y: 80, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        exit={{ y: 80, opacity: 0 }}
                        transition={{ type: 'spring', stiffness: 320, damping: 28 }}
                    >
                        <div className={styles.bulkLeft}>
                            <span className={styles.bulkCount}>{selectedItems.length} selected</span>
                        </div>
                        <div className={styles.bulkActions}>
                            <button
                                className={`${styles.bulkBtn} ${bulkPanel === 'category' ? styles.bulkBtnActive : ''}`}
                                onClick={() => setBulkPanel(p => p === 'category' ? null : 'category')}
                            >
                                <Tag size={14} /> Assign Category
                            </button>
                            <button
                                className={`${styles.bulkBtn} ${bulkPanel === 'hsn' ? styles.bulkBtnActive : ''}`}
                                onClick={() => setBulkPanel(p => p === 'hsn' ? null : 'hsn')}
                            >
                                <CheckCheck size={14} /> Set HSN Code
                            </button>
                            <button className={styles.bulkDismissBtn} onClick={clearSelection}>
                                <Trash2 size={13} /> Deselect All
                            </button>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* ── Bulk Panel Drawer ── */}
            <AnimatePresence>
                {bulkPanel && selectedItems.length > 0 && (
                    <motion.div
                        className={styles.bulkPanel}
                        initial={{ y: 140, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        exit={{ y: 140, opacity: 0 }}
                        transition={{ type: 'spring', stiffness: 280, damping: 28 }}
                    >
                        <div className={styles.bulkPanelInner}>
                            {bulkPanel === 'hsn' && (
                                <>
                                    <div className={styles.bulkPanelLabel}>
                                        Set HSN code for all <strong>{selectedItems.length}</strong> selected product(s)
                                    </div>
                                    <div className={styles.bulkPanelRow}>
                                        <div className={styles.bulkHsnSelectWrap}>
                                            <Select
                                                classNamePrefix="hsn-select"
                                                styles={selectStyles}
                                                options={hsnOptions}
                                                isLoading={isHsnCodesLoading}
                                                isClearable={false}
                                                menuPlacement="top"
                                                menuPosition="fixed"
                                                menuShouldScrollIntoView={false}
                                                menuPortalTarget={document.body}
                                                placeholder="Select HSN code…"
                                                value={hsnOptions.find(option => option.value === bulkHsnValue) || null}
                                                onChange={(option) => setBulkHsnValue(option?.value || '')}
                                                noOptionsMessage={() => isHsnCodesLoading ? 'Loading HSN codes…' : 'No HSN codes found'}
                                            />
                                        </div>
                                        <button
                                            className={styles.bulkApplyBtn}
                                            onClick={applyBulkHsn}
                                            disabled={isApplyingBulk}
                                        >
                                            {isApplyingBulk ? '…' : 'Apply to All'}
                                        </button>
                                        <button className={styles.bulkCloseBtn} onClick={() => setBulkPanel(null)}>
                                            <X size={15} />
                                        </button>
                                    </div>
                                </>
                            )}
                            {bulkPanel === 'category' && (
                                <>
                                    <div className={styles.bulkPanelLabel}>
                                        Assign category to <strong>{selectedItems.length}</strong> selected product(s)
                                    </div>
                                    <div className={styles.bulkPanelRow}>
                                        <div className={styles.bulkHsnSelectWrap}>
                                            <Select
                                                classNamePrefix="hsn-select"
                                                styles={selectStyles}
                                                options={categorySelectOptions}
                                                isLoading={isCategoriesLoading}
                                                isDisabled={isCategoriesLoading}
                                                isClearable={false}
                                                menuPlacement="top"
                                                menuPosition="fixed"
                                                menuShouldScrollIntoView={false}
                                                menuPortalTarget={document.body}
                                                placeholder={isCategoriesLoading ? 'Loading categories…' : 'Select category…'}
                                                value={selectedCategoryOption}
                                                onChange={(option) => setBulkCategoryValue(option?.value || '')}
                                                noOptionsMessage={() => isCategoriesLoading ? 'Loading categories…' : 'No categories found'}
                                            />
                                        </div>
                                        <button
                                            className={styles.bulkApplyBtn}
                                            onClick={applyBulkCategory}
                                            disabled={isApplyingBulk}
                                        >
                                            {isApplyingBulk ? '…' : 'Apply to All'}
                                        </button>
                                        <button className={styles.bulkCloseBtn} onClick={() => setBulkPanel(null)}>
                                            <X size={15} />
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    )
}

export default ImHome